import { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Send, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type CampaignTestSendDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  campaignName: string;
  smsEnabled: boolean;
  onSent?: () => void;
};

type TestStep = {
  id: string;
  step_type: string;
  subject: string | null;
  body: string | null;
  step_number: number;
};

export function CampaignTestSendDialog({ open, onOpenChange, campaignId, campaignName, smsEnabled, onSent }: CampaignTestSendDialogProps) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [steps, setSteps] = useState<TestStep[]>([]);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");

  useEffect(() => {
    if (!open || !campaignId) return;
    void loadFirstSteps();
  }, [open, campaignId]);

  const emailStep = useMemo(() => steps.find((step) => step.step_type === "email"), [steps]);
  const smsStep = useMemo(() => steps.find((step) => step.step_type === "sms"), [steps]);

  const loadFirstSteps = async () => {
    setLoading(true);
    try {
      const { data: campaign, error: campaignError } = await supabase
        .from("ai_campaigns")
        .select("campaign_variants(sequence_id)")
        .eq("id", campaignId)
        .single();
      if (campaignError) throw campaignError;

      const sequenceId = campaign?.campaign_variants?.find((variant: any) => variant.sequence_id)?.sequence_id;
      if (!sequenceId) {
        setSteps([]);
        return;
      }

      const { data, error } = await supabase
        .from("sequence_steps")
        .select("id, step_type, subject, body, step_number")
        .eq("sequence_id", sequenceId)
        .in("step_type", ["email", "sms"])
        .order("step_number", { ascending: true });
      if (error) throw error;
      setSteps((data || []) as TestStep[]);
    } catch (error: any) {
      toast.error(error.message || "Could not load campaign test content");
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!emailStep) return toast.error("This campaign has no email step to test");
    if (!testEmail) return toast.error("Enter a test email address");
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-test-email", {
        body: {
          test_email: testEmail,
          subject: emailStep.subject || `${campaignName} test`,
          body_html: emailStep.body || "",
          campaign_id: campaignId,
          sequence_step_id: emailStep.id,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Test email sent");
      onSent?.();
    } catch (error: any) {
      toast.error(error.message || "Test email failed");
    } finally {
      setSending(false);
    }
  };

  const sendTestSms = async () => {
    if (!smsStep) return toast.error("This campaign has no SMS step to test");
    if (!smsEnabled) return toast.error("SMS setup is required before sending test texts");
    if (!testPhone) return toast.error("Enter a test phone number");
    toast.error("Live SMS test sending will unlock after Twilio is connected");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-primary" />Send campaign test</DialogTitle>
          <DialogDescription>Send the first email or text step to yourself before enrolling real leads.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading test content...</div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-3 rounded-md border p-4">
              <Label className="flex items-center gap-2"><Mail className="h-4 w-4" />Test email</Label>
              <Input type="email" value={testEmail} onChange={(event) => setTestEmail(event.target.value)} placeholder="you@example.com" disabled={!emailStep || sending} />
              <Textarea value={emailStep?.subject || "No email step available"} readOnly className="min-h-[42px] resize-none text-xs" />
              <Button size="sm" onClick={sendTestEmail} disabled={!emailStep || sending}>{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Send test email</Button>
            </div>

            <div className="space-y-3 rounded-md border p-4">
              <Label className="flex items-center gap-2"><Smartphone className="h-4 w-4" />Test SMS</Label>
              <Input value={testPhone} onChange={(event) => setTestPhone(event.target.value)} placeholder="+15551234567" disabled={!smsStep || !smsEnabled} />
              <Textarea value={smsStep?.body || (smsEnabled ? "No SMS step available" : "SMS setup required before live text tests")} readOnly className="min-h-[70px] resize-none text-xs" />
              <Button size="sm" variant="outline" onClick={sendTestSms} disabled={!smsStep || !smsEnabled}><Smartphone className="mr-2 h-4 w-4" />Send test SMS</Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}